import type { DictationOriginal } from "./dictation";
import { api } from "./client";

/**
 * Client for bella-admin-api's tickets module (`/v1/tickets/*`): the Tickets
 * screen and the ticket panel. Contract in bella's `docs/modules/tickets.md`.
 *
 * `tickets.view` opens and works any ticket; moving someone else's ticket
 * needs `tickets.manage` (403 NOT_YOUR_TICKET). A 403 on a read means "not
 * your screen".
 */

export const TICKET_SCOPES = ["mine", "unassigned", "everyone", "person"] as const;
export type TicketScope = (typeof TICKET_SCOPES)[number];

export const TICKET_VIEWS = ["todo", "waiting", "old", "closed"] as const;
export type TicketView = (typeof TICKET_VIEWS)[number];

export type TicketState =
  "needs_reply" | "follow_up_due" | "ai_answering" | "waiting" | "stale" | "closed";

export type TicketReason =
  "new" | "customer_replied" | "ai_handed_over" | "reopened" | "follow_up_due";

export interface Named {
  id: number;
  name: string;
}

export interface TicketPerson {
  kind: "customer" | "champ";
  id: number;
  name: string;
  /** "••••• •4821" — the panel never receives a whole number. */
  phone: string;
}

export interface TicketLastMessage {
  at: number;
  from: "customer" | "team" | "ai" | "automation";
  preview: string;
  viaWhatsApp: boolean;
}

export interface TicketRow {
  id: number;
  number: string;
  title: string;
  person: TicketPerson | null;
  raisedByChamp: Named | null;
  category: Named | null;
  subcategory: Named | null;
  source: Named | null;
  visibleToCustomer: boolean;
  state: TicketState;
  reason: TicketReason | null;
  /** Epoch ms the customer started waiting on us. */
  waitingSince: number | null;
  /** IST "YYYY-MM-DD HH:mm:ss", or null. */
  followUpAt: string | null;
  important: boolean;
  owner: Named | null;
  helpers: Named[];
  aiAnswering: boolean;
  lastMessage: TicketLastMessage | null;
  otherOpenCount: number;
  /** Minutes we aim to reply within (the category's, else the default 30). */
  targetMinutes: number;
  updatedAt: number;
  createdAt: number;
}

/** Why a ticket was closed. "stale" exists only for bulk close (Old view). */
export const TICKET_CLOSE_REASONS = [
  "resolved",
  "no_response",
  "duplicate",
  "not_valid",
  "other",
] as const;
export type TicketCloseReason = (typeof TICKET_CLOSE_REASONS)[number];
export type BulkCloseReason = TicketCloseReason | "stale";

export interface TicketCounts {
  todo: number;
  waiting: number;
  /** Of todo: waiting over 30 minutes, or a follow-up overdue. */
  late: number;
  old: number;
}

export interface TicketList {
  rows: TicketRow[];
  counts: TicketCounts;
  now: number;
}

export interface MyTicketCounts extends TicketCounts {
  unassigned: number;
}

export interface TicketPlan {
  subscriptionId: number;
  vehicleNumber: string;
  vehicleModel: string | null;
  status: "active" | "paused" | "inactive" | "payment_pending";
  planName: string | null;
  frequency: string | null;
  timeSlot: string | null;
  champFirstName: string | null;
  address: string | null;
}

export interface TicketCustomer {
  id: number;
  name: string;
  phone: string;
  plans: TicketPlan[];
  agent: Named | null;
  coins: number | null;
}

export interface TicketJob {
  jobId: number;
  /** "YYYY-MM-DD". */
  date: string;
  weekday: string;
  vehicleNumber: string | null;
  service: "exterior" | "full" | "interior" | "other";
  /** done, not_done, reported, not_at_home, under_review, … */
  status: string;
  reason: string | null;
  champName: string | null;
  /** Null when the job has no champ, or an older API did not send it. */
  champId: number | null;
  photoUrl: string | null;
}

export interface TicketHandOver {
  at: number;
  code: string | null;
  reason: string;
  note: string | null;
}

export interface TicketOther {
  id: number;
  number: string;
  title: string;
  state: TicketState;
  ownerName: string | null;
  updatedAt: number;
}

export interface TicketDetail extends TicketRow {
  status: number;
  statusName: string;
  customer: TicketCustomer | null;
  job: TicketJob | null;
  handOver: TicketHandOver | null;
  createdByName: string | null;
  /** note_only when the customer cannot see the ticket. */
  composer: "reply_and_note" | "note_only";
  /** A complaint that cannot close until its job is tagged. */
  closeNeedsJob: boolean;
  otherTickets: TicketOther[];
  /** The customer's rating of how it was handled, when they gave one. */
  rating: { rating: number; comment: string | null; at: number } | null;
  /** The latest close, kept after a reopen too. */
  lastClosure: {
    reason: BulkCloseReason;
    note: string | null;
    byName: string | null;
    at: number;
  } | null;
  /** The category's AI reply mode. */
  aiReplyMode: "off" | "draft" | "auto";
}

export interface TicketEntry {
  id: number;
  at: number;
  /** message: the conversation; note: staff only; event: a change. */
  kind: "message" | "note" | "event";
  side: "them" | "us";
  author: {
    kind: "customer" | "staff" | "ai" | "automation" | "champ";
    id: number | null;
    name: string;
  };
  text: string;
  buttons: string[];
  attachment: { kind: "image" | "video" | "audio" | "file"; url: string } | null;
  locationUrl: string | null;
  viaWhatsApp: boolean;
  /** A WhatsApp send the provider refused, or that failed later. Replies are always false. */
  failed: boolean;
  original: DictationOriginal | null;
  repliedToId: number | null;
}

/** Another person typing on this ticket right now (not the caller). */
export interface TicketReplying {
  id: number;
  name: string;
  mode: "reply" | "note";
}

export interface TicketEntries {
  entries: TicketEntry[];
  truncated: boolean;
  replying: TicketReplying[];
}

export interface TicketCategory extends Named {
  audience: "customer" | "champ" | "internal";
  subcategories: Named[];
}

export interface SavedReply {
  id: number;
  title: string;
  body: string;
  group: string;
}

export interface TicketLookups {
  categories: TicketCategory[];
  sources: Named[];
  savedReplies: SavedReply[];
}

export interface TicketAgent extends Named {
  open: number;
  todo: number;
}

export interface PersonTickets {
  person: TicketPerson;
  tickets: TicketRow[];
}

export type JobCheckOutcome =
  | "review_opened"
  | "complaint_added"
  | "no_action"
  | "job_already_complained"
  | "payout_already_generated"
  | "job_replaced"
  | "checked_by_old_panel"
  | "not_applicable"
  | "already_checked"
  | "job_changed";

/** What the complaint job check did when a ticket was closed or filed. */
export interface TicketJobCheck {
  outcome: JobCheckOutcome;
  jobId: number | null;
  reversedAmount: number;
  payKeptByTeamLeader: boolean;
  /** Written for staff. */
  message: string;
}

export interface TicketActionResult {
  ticketId: number;
  /** Null when no job check ran. */
  jobCheck: TicketJobCheck | null;
  /** Closing or filing cleared the customer's "frequently missed" tag. */
  frequentlyMissedTagRemoved: boolean;
  /** The reschedule walk tagged this earlier job. */
  taggedJobId?: number;
}

export interface TicketAttachmentUpload {
  key: string;
  url: string;
  kind: "photo" | "file";
  name: string;
}

export interface TicketReplyAttachment {
  key: string;
  kind: "photo" | "file";
}

export interface TicketWhatsAppThread {
  threadId: number;
  numberName: string;
  provider: "wati" | "chatzy";
  canSendText: boolean;
  /** Why text cannot be sent (24-hour window, provider rules). */
  reason: string | null;
  lastInboundAt: number | null;
}

export interface TicketWhatsApp {
  threads: TicketWhatsAppThread[];
  defaultThreadId: number | null;
}

export interface TicketAssist {
  /** The category's AI mode is "draft": offer a suggestion on opening. */
  autoDraft: boolean;
  /** 12 or more visible messages. */
  summaryAvailable: boolean;
}

export interface TicketSummary {
  summary: string | null;
  upToEntryId: number;
}

export type BulkTicketInput =
  | { ticketIds: number[]; action: "assign"; adminId: number }
  | { ticketIds: number[]; action: "follow_up"; at: string | null }
  | { ticketIds: number[]; action: "priority"; important: boolean }
  | {
      ticketIds: number[];
      action: "close";
      reason?: BulkCloseReason;
      note?: string;
    };

export interface BulkTicketResult {
  done: number[];
  skipped: { ticketId: number; reason: string }[];
}

/** One bulk call takes 1 to this many tickets. */
export const BULK_TICKETS_MAX = 200;

export interface TicketListParams {
  scope: TicketScope;
  adminId?: number | undefined;
  view: TicketView;
  q?: string | undefined;
  categoryIds?: number[] | undefined;
  sourceIds?: number[] | undefined;
}

export interface CreateTicketInput {
  customerId: number;
  title: string;
  categoryId: number;
  subcategoryId?: number;
  visibleToCustomer: boolean;
  message?: string;
  original?: DictationOriginal;
  ownerAdminId?: number;
  followUpAt?: string | null;
  sourceId?: number;
  jobId?: number;
}

/** A ticket title: the DTO's own limit. */
export const TICKET_TITLE_MAX = 150;
/** A reply or note. */
export const TICKET_TEXT_MAX = 10_000;

export const ticketKeys = {
  all: ["tickets"] as const,
  lists: () => [...ticketKeys.all, "list"] as const,
  list: (params: TicketListParams) => [...ticketKeys.lists(), params] as const,
  counts: () => [...ticketKeys.all, "counts"] as const,
  detail: (id: number) => [...ticketKeys.all, "detail", id] as const,
  entries: (id: number) => [...ticketKeys.all, "entries", id] as const,
  jobs: (id: number) => [...ticketKeys.all, "jobs", id] as const,
  person: (customerId: number) => [...ticketKeys.all, "person", customerId] as const,
  lookups: () => [...ticketKeys.all, "lookups"] as const,
  agents: () => [...ticketKeys.all, "agents"] as const,
  customers: (search: string) => [...ticketKeys.all, "customers", search] as const,
  whatsapp: (id: number) => [...ticketKeys.all, "whatsapp", id] as const,
  // Outside `all` on purpose: every ticket action invalidates `all`, and these
  // calls cost an AI request (summary) or are cached for the ticket's life.
  assist: (id: number) => ["ticket-ai", "assist", id] as const,
  summary: (id: number) => ["ticket-ai", "summary", id] as const,
};

/** Attachments: what the reply box accepts, checked before it uploads. */
export const TICKET_ATTACHMENT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;
export const TICKET_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

export function listTickets(params: TicketListParams, signal?: AbortSignal) {
  const query = new URLSearchParams({ scope: params.scope, view: params.view });
  if (params.adminId) query.set("adminId", String(params.adminId));
  if (params.q) query.set("q", params.q);
  if (params.categoryIds?.length) query.set("categoryIds", params.categoryIds.join(","));
  if (params.sourceIds?.length) query.set("sourceIds", params.sourceIds.join(","));
  return api.get<TicketList>(`/tickets?${query.toString()}`, withSignal(signal));
}

export const getMyTicketCounts = (signal?: AbortSignal) =>
  api.get<MyTicketCounts>("/tickets/counts", withSignal(signal));

export const getTicket = (id: number, signal?: AbortSignal) =>
  api.get<TicketDetail>(`/tickets/${id}`, withSignal(signal));

export const getTicketEntries = (id: number, signal?: AbortSignal) =>
  api.get<TicketEntries>(`/tickets/${id}/entries`, withSignal(signal));

export const getTicketJobs = (id: number, signal?: AbortSignal) =>
  api.get<TicketJob[]>(`/tickets/${id}/jobs`, withSignal(signal));

export const getCustomerTickets = (customerId: number, signal?: AbortSignal) =>
  api.get<PersonTickets>(`/tickets/people/customer/${customerId}`, withSignal(signal));

export const getTicketLookups = (signal?: AbortSignal) =>
  api.get<TicketLookups>("/tickets/lookups", withSignal(signal));

export interface CustomerMatch {
  id: number;
  name: string;
  phone: string;
  openTickets: number;
}

/** By phone (10 digits, +91 allowed) or customer number. */
export const findTicketCustomers = (search: string, signal?: AbortSignal) =>
  api.get<CustomerMatch[]>(
    `/tickets/customers?search=${encodeURIComponent(search)}`,
    withSignal(signal),
  );

export const getTicketAgents = (signal?: AbortSignal) =>
  api.get<TicketAgent[]>("/tickets/agents", withSignal(signal));

export const replyToTicket = (
  id: number,
  input: {
    text: string;
    original?: DictationOriginal;
    attachment?: TicketReplyAttachment;
  },
) => api.post<TicketActionResult>(`/tickets/${id}/replies`, input);

export const addTicketNote = (
  id: number,
  input: { text: string; original?: DictationOriginal; mentionAdminIds?: number[] },
) => api.post<TicketActionResult>(`/tickets/${id}/notes`, input);

export const setTicketOwner = (id: number, adminId: number) =>
  api.put<TicketActionResult>(`/tickets/${id}/owner`, { adminId });

export const takeTicket = (id: number) =>
  api.post<TicketActionResult>(`/tickets/${id}/take`, {});

export const setTicketHelpers = (id: number, adminIds: number[]) =>
  api.put<TicketActionResult>(`/tickets/${id}/helpers`, { adminIds });

export const setTicketFollowUp = (id: number, at: string | null) =>
  api.put<TicketActionResult>(`/tickets/${id}/follow-up`, { at });

export const setTicketPriority = (id: number, important: boolean) =>
  api.put<TicketActionResult>(`/tickets/${id}/priority`, { important });

export const fileTicket = (
  id: number,
  input: { categoryId: number; subcategoryId?: number; jobId?: number },
) => api.put<TicketActionResult>(`/tickets/${id}/filing`, input);

export const closeTicket = (
  id: number,
  input: {
    jobId?: number;
    message?: string;
    original?: DictationOriginal;
    reason?: TicketCloseReason;
    note?: string;
  },
) => api.post<TicketActionResult>(`/tickets/${id}/close`, input);

/** Closes `id` as a duplicate of `intoTicketId` (the same person's). */
export const mergeTicket = (id: number, intoTicketId: number) =>
  api.post<TicketActionResult>(`/tickets/${id}/merge`, { intoTicketId });

/** Manage only. One ticket's failure never stops the rest. */
export const bulkTickets = (input: BulkTicketInput) =>
  api.post<BulkTicketResult>("/tickets/bulk", input);

export const getTicketWhatsApp = (id: number, signal?: AbortSignal) =>
  api.get<TicketWhatsApp>(`/tickets/${id}/whatsapp`, withSignal(signal));

export const sendTicketWhatsApp = (
  id: number,
  input: { threadId: number; text: string; clientMessageId: string },
) =>
  api.post<TicketActionResult & { messageId: number }>(
    `/tickets/${id}/whatsapp-replies`,
    input,
  );

/** A photo or PDF for a reply; the reply then carries `{ key, kind }`. */
export function uploadTicketAttachment(id: number, file: File) {
  const form = new FormData();
  form.append("file", file, file.name);
  return api.post<TicketAttachmentUpload>(`/tickets/${id}/attachments`, form);
}

/** A heartbeat while someone types; the server keeps it for 15 s. */
export const sendTicketReplying = (id: number, mode: "reply" | "note") =>
  api.post<null>(`/tickets/${id}/replying`, { mode });

export const getTicketAssist = (id: number, signal?: AbortSignal) =>
  api.get<TicketAssist>(`/tickets/${id}/assist`, withSignal(signal));

export const suggestTicketReply = (id: number) =>
  api.post<{ text: string }>(`/tickets/${id}/suggested-reply`, {});

export const getTicketSummary = (id: number, signal?: AbortSignal) =>
  api.get<TicketSummary>(`/tickets/${id}/summary`, withSignal(signal));

export const reopenTicket = (id: number) =>
  api.post<TicketActionResult>(`/tickets/${id}/reopen`, {});

export const takeOverFromAi = (id: number) =>
  api.post<TicketActionResult>(`/tickets/${id}/take-over`, {});

export const createTicket = (input: CreateTicketInput) =>
  api.post<{ ticketId: number; ticketNumber: string }>("/tickets", input);
